/**
 * Scan & Pay helpers. Kept in step with the Flutter app
 * (lib/features/pay/domain) so both read and write the same data.
 *
 * A UPI request is a plain object: { vpa, name, amount, note, qrParams, rawQuery }.
 * qrParams holds every parameter of a scanned QR (lower-cased keys);
 * rawQuery is the scanned QR's query string exactly as printed, which the
 * payment link reuses (re-encoding values, e.g. "@" as "%40", made some
 * payments fail). Signed merchant QRs (`sign`) are sent unchanged.
 */

const VPA_PATTERN = /^[a-zA-Z0-9._-]{1,256}@[a-zA-Z][a-zA-Z0-9.-]{1,63}$/;

export const isValidVpa = (value) => VPA_PATTERN.test((value || '').trim());

export const isSignedQr = (req) => !!req?.qrParams && 'sign' in req.qrParams;

/** The QR fixed the amount, so the user should not edit it. */
export const isAmountLocked = (req) => isSignedQr(req) && 'am' in req.qrParams;

export const payeeDisplayName = (req) => (req?.name || '').trim() || req?.vpa || '';

const decodePart = (s) => {
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    return s;
  }
};

/** Parses a scanned QR. Returns null when it is not a UPI payment QR. */
export function parseUpiQr(text) {
  const t = (text || '').trim();
  if (!t.toLowerCase().startsWith('upi://')) return null;
  const q = t.indexOf('?');
  if (q === -1) return null;
  const params = {};
  for (const pair of t.slice(q + 1).split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const key = decodePart(eq === -1 ? pair : pair.slice(0, eq)).toLowerCase();
    if (key) params[key] = eq === -1 ? '' : decodePart(pair.slice(eq + 1));
  }
  const vpa = params.pa?.trim();
  if (!vpa || !isValidVpa(vpa)) return null;
  const amount = parseFloat(params.am);
  return {
    vpa,
    name: params.pn?.trim() || null,
    amount: amount > 0 ? amount : null,
    note: params.tn?.trim() || null,
    qrParams: params,
    rawQuery: t.slice(q + 1)
  };
}

/** Percent-encodes a value but keeps "@" (UPI IDs); spaces become %20, since some UPI apps show "+" literally. */
const encodeValue = (value) => encodeURIComponent(value).replace(/%40/g, '@');

/**
 * Some UPI apps reject special characters in the note, so it is sent as
 * letters, digits and single spaces only, at most 50 characters. The
 * expense keeps the full text.
 */
export function cleanUpiNote(note) {
  const text = (note || '').replace(/[^A-Za-z0-9 ]/g, ' ').replace(/ +/g, ' ').trim();
  return text.length > 50 ? text.slice(0, 50).trim() : text;
}

/**
 * Query string for the payment link. req.note is the user's description
 * (never the payee's name) and becomes the cleaned UPI note.
 *
 * Scanned QR: sent exactly as printed, with the amount and note set (signed
 * QRs are not changed at all) and INR added if missing.
 * Typed UPI ID: built from our fields.
 */
export function upiQueryString(req) {
  const tn = cleanUpiNote(req.note);
  const raw = req.rawQuery;
  if (raw) {
    if (isSignedQr(req)) return raw;
    const amount = req.amount ? Number(req.amount).toFixed(2) : null;
    let hasAmount = false;
    let hasNote = false;
    let hasCurrency = false;
    const out = [];
    for (const part of raw.split('&')) {
      if (!part) continue;
      const key = part.split('=')[0].toLowerCase();
      if (key === 'am') {
        hasAmount = true;
        out.push(amount ? `am=${amount}` : part);
        continue;
      }
      if (key === 'tn') {
        hasNote = true;
        out.push(tn ? `tn=${encodeValue(tn)}` : part);
        continue;
      }
      if (key === 'cu') hasCurrency = true;
      out.push(part);
    }
    if (!hasAmount && amount) out.push(`am=${amount}`);
    if (!hasNote && tn) out.push(`tn=${encodeValue(tn)}`);
    if (!hasCurrency) out.push('cu=INR');
    return out.join('&');
  }
  const params = { pa: req.vpa };
  if (req.name) params.pn = req.name;
  if (req.amount) params.am = Number(req.amount).toFixed(2);
  if (tn) params.tn = tn;
  params.cu = 'INR';
  return Object.entries(params)
    .map(([k, v]) => `${k}=${encodeValue(v)}`)
    .join('&');
}

export const upiUri = (req, prefix = 'upi://pay') => `${prefix}?${upiQueryString(req)}`;

/** Phones and tablets can hand a upi:// link to an installed UPI app. */
export function canOpenUpiApps() {
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS reports itself as a Mac.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

// ---------------------------------------------------------------------------
// Payee line in transaction notes:
// "Paid via UPI to Ravi Kumar (ravi@okhdfc)" or "Paid via UPI to ravi@okhdfc".
// ---------------------------------------------------------------------------

const NOTE_PREFIX = 'Paid via UPI to ';
const NOTE_WITH_NAME = /^Paid via UPI to (.+) \(([^()\s]+@[^()\s]+)\)$/;
const NOTE_VPA_ONLY = /^Paid via UPI to ([^()\s]+@[^()\s]+)$/;

export function upiNoteLine(vpa, name) {
  const n = (name || '').trim();
  return n ? `${NOTE_PREFIX}${n} (${vpa})` : `${NOTE_PREFIX}${vpa}`;
}

/** Splits notes into what the user typed and the UPI payee, if any. */
export function splitUpiNote(notes) {
  const lines = (notes || '').split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    const named = line.match(NOTE_WITH_NAME);
    const plain = named ? null : line.match(NOTE_VPA_ONLY);
    if (!named && !plain) continue;
    const rest = lines.filter((_, j) => j !== i);
    return {
      userNotes: rest.join('\n').trim(),
      vpa: named ? named[2] : plain[1],
      name: named ? named[1] : null
    };
  }
  return { userNotes: (notes || '').trim(), vpa: null, name: null };
}

/** Joins the user's notes with the payee line. Empty string when both are empty. */
export function joinUpiNote(userNotes, vpa, name) {
  const parts = [];
  if ((userNotes || '').trim()) parts.push(userNotes.trim());
  if ((vpa || '').trim()) parts.push(upiNoteLine(vpa.trim(), name));
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Category guess for a UPI payment
// ---------------------------------------------------------------------------

/** Keyword -> category name fragments ("Food & Dining" matches "food"). */
const KEYWORD_HINTS = [
  [['swiggy', 'zomato', 'restaurant', 'cafe', 'coffee', 'chai', 'tea', 'lunch',
    'dinner', 'breakfast', 'food', 'pizza', 'burger', 'bakery', 'dhaba',
    'biryani', 'snack', 'canteen', 'mess'], ['food', 'dining', 'restaurant', 'eat']],
  [['grocery', 'groceries', 'kirana', 'mart', 'bigbasket', 'blinkit', 'zepto',
    'dmart', 'instamart', 'supermarket', 'vegetable', 'sabzi', 'milk', 'dairy',
    'fruit'], ['grocer', 'food']],
  [['uber', 'ola', 'rapido', 'metro', 'cab', 'taxi', 'auto', 'irctc', 'railway',
    'bus', 'petrol', 'fuel', 'diesel', 'parking', 'toll', 'fastag'],
    ['travel', 'transport', 'fuel', 'commute']],
  [['amazon', 'flipkart', 'myntra', 'ajio', 'meesho', 'nykaa', 'store', 'shop',
    'fashion', 'mall'], ['shopping']],
  [['electricity', 'recharge', 'jio', 'airtel', 'bsnl', 'broadband', 'wifi',
    'gas', 'water', 'bill', 'dth'], ['bill', 'utilit', 'recharge']],
  [['pharmacy', 'medical', 'chemist', 'hospital', 'clinic', 'apollo', 'medplus',
    'pharmeasy', 'doctor', 'lab'], ['health', 'medical']],
  [['rent', 'landlord', 'hostel'], ['rent', 'housing']],
  [['netflix', 'hotstar', 'bookmyshow', 'pvr', 'inox', 'spotify', 'movie'],
    ['entertain', 'movie', 'subscription']],
  [['school', 'college', 'tuition', 'course', 'fees', 'books'], ['education']]
];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Picks an expense category for a UPI payment, in this order:
 * 1. the category used last time for this UPI ID,
 * 2. a past transaction with the same description,
 * 3. keywords in the payee name or note,
 * 4. the most-used expense category, then "Other Expense".
 * Returns { categoryId, reason } or null.
 */
export function guessUpiCategory({ categories, rememberedCategoryId, payeeName, note, descriptions = [], topCategories = [] }) {
  if (!categories?.length) return null;
  const byId = new Map(categories.map((c) => [c._id, c]));
  const who = (payeeName || '').trim();

  if (rememberedCategoryId && byId.has(rememberedCategoryId)) {
    return {
      categoryId: rememberedCategoryId,
      reason: who ? `Same as your last payment to ${who}` : 'Same as your last payment here'
    };
  }

  const noteLower = (note || '').trim().toLowerCase();
  if (noteLower) {
    const match = descriptions.find((s) =>
      s.categoryId && (!s.type || s.type === 'expense') &&
      (s.description || '').trim().toLowerCase() === noteLower &&
      byId.has(s.categoryId)
    );
    if (match) return { categoryId: match.categoryId, reason: `Used before for "${note.trim()}"` };
  }

  const text = `${who} ${noteLower}`.toLowerCase();
  if (text.trim()) {
    for (const [keywords, hints] of KEYWORD_HINTS) {
      const word = keywords.find((k) => new RegExp(`\\b${escapeRegExp(k)}`).test(text));
      if (!word) continue;
      for (const hint of hints) {
        const cat = categories.find((c) => c.name.toLowerCase().includes(hint));
        if (cat) return { categoryId: cat._id, reason: `Matched from "${word}"` };
      }
    }
  }

  // The web API returns most-used categories by name.
  for (const name of topCategories) {
    const cat = categories.find((c) => c.name === name);
    if (cat) return { categoryId: cat._id, reason: null };
  }
  for (const name of ['other expense', 'other']) {
    const cat = categories.find((c) => c.name.toLowerCase() === name);
    if (cat) return { categoryId: cat._id, reason: null };
  }
  return { categoryId: categories[0]._id, reason: null };
}

/** Scan & Pay is for UPI, so it shows only when the currency in Settings is INR (India). */
export const isScanPayAvailable = (config) => (config?.currency || 'INR').toUpperCase() === 'INR';


/** iPhone / iPad: iOS has no app chooser for upi://, so we link each app directly. */
export function isIosDevice() {
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/**
 * UPI apps with their own iOS payment links (same as the Flutter app). BHIM
 * is left out: it rejects payment links to a person's UPI ID from other
 * apps, and the plain upi:// link may open it, so that is not offered.
 */
export const IOS_UPI_APPS = [
  { name: 'GPay', prefix: 'tez://upi/pay', color: '#4285F4', initial: 'G' },
  { name: 'PhonePe', prefix: 'phonepe://pay', color: '#5F259F', initial: 'P' },
  { name: 'Paytm', prefix: 'paytmmp://pay', color: '#00B9F1', initial: 'P' }
];
